import React, { useCallback, useContext, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Row } from 'reactstrap';
import { ReadObject } from "services/storage";
import { consumeSessionExpiredNotice } from "services/session";
import { ReadIdentitySession, IDENTITY_AREA_ROUTES } from 'services/identity';
import { AREA_SELECTION_PATH } from 'services/unifiedLogin';
import { ThemedComponent } from "ui/theme";
import { CoreContext } from "context/CoreContext";

import {
    FormContent,
    Content,
    HeaderContainer,
    StyledCol,
    AcceleratorBrand,
    AcceleratorBrandName,
    AcceleratorBrandProgram,
} from './styled'
import { Icon } from "ui/styled";

export default function ContainerUnauthenticated({ children, login, header, success, lightBlue, students, portal, noHeader }) {

    const history = useHistory();
    const navigate = useCallback((to) => history.push(`/${to}`), [history]);
    const { themeMode, translation } = useContext(CoreContext);
    const resolvedPortal = portal || (students ? 'essentials' : null);
    const completeNext = useCallback(() => { navigate('dashboard') }, [navigate])

    const init = useCallback(() => {
        if (success) return;
        if (new URLSearchParams(window.location.search).get('reason') === 'session-expired') return
        if (['/', '/login'].includes(window.location.pathname) && ['authentication', 'mvu_essentials', 'mvu_accelerator'].some(key => ReadObject(key)?.jwt)) {
            history.replace(AREA_SELECTION_PATH);
            return;
        }
        const identity = ReadIdentitySession();
        if (identity?.centralUserId) {
            const area = resolvedPortal === 'essentials' ? 'lite' : resolvedPortal === 'accelerator' ? 'accelerator' : 'universe';
            if (!success && identity.areas?.includes(area)) history.push(IDENTITY_AREA_ROUTES[area]);
            return;
        }

        const authenticationMvuEssentials = ReadObject('mvu_essentials')
        const authenticationMvuAccelerator = ReadObject('mvu_accelerator')

        if (authenticationMvuAccelerator?.jwt && !success) {
            navigate('mvu-accelerator')
            return
        }

        if (resolvedPortal === 'essentials') {
            if (authenticationMvuEssentials?.jwt && !success) {
                navigate('mvu-lite')
                return
            }
            return
        }

        if (resolvedPortal === 'accelerator') {
            if (authenticationMvuAccelerator?.jwt && !success) {
                navigate('mvu-accelerator')
                return
            }
            return
        }

        if (authenticationMvuEssentials?.jwt && !success) {
            navigate('mvu-lite')
            return
        }

        const authentication = ReadObject('authentication')
        if (authentication?.jwt && !success) {
            completeNext()
            return
        }
    }, [completeNext, navigate, resolvedPortal, success, history])

    useEffect(() => {
        init()
        window.scrollTo(0, 0)
    }, [init])

    useEffect(() => {
        if (!consumeSessionExpiredNotice()) return

        toast.warning(
            translation?.translations?.session_expired_sign_in_again
            || 'Your session has expired. Please sign in again to continue.',
            { toastId: 'session-expired' }
        )
    }, [translation])

    const homeRoute = resolvedPortal === 'accelerator'
        ? 'mvu-accelerator/login'
        : resolvedPortal === 'essentials'
            ? 'mvu-lite/login'
            : ''
    const acceleratorBrandName = translation?.translations?.accelerator_brand_name || 'MagVenture'
    const acceleratorBrandProgram = translation?.translations?.accelerator_brand_program || 'Accelerator Program'

    return (
        <>
            <ThemedComponent>
                <Content>
                    <Row className="noPadding">
                        <StyledCol md={{ size: 12 }} className="noPadding" full lightBlue={lightBlue}>
                            {noHeader ? null : (
                                <HeaderContainer>
                                    {resolvedPortal === 'accelerator' ? (
                                        <AcceleratorBrand
                                            onClick={() => navigate(homeRoute)}
                                            aria-label={`${acceleratorBrandName} ${acceleratorBrandProgram}`}
                                        >
                                            <AcceleratorBrandName>{acceleratorBrandName}</AcceleratorBrandName>
                                            <AcceleratorBrandProgram>{acceleratorBrandProgram}</AcceleratorBrandProgram>
                                        </AcceleratorBrand>
                                    ) : (
                                        <Icon icon={themeMode === 'light' ? 'MV_Universe_Logo' : 'MV_Universe_Logo_white'} width={320} onClick={() => navigate(homeRoute)} pointer />
                                    )}
                                </HeaderContainer>
                            )}
                            <FormContent login={login} header={header} noHeader={noHeader}>
                                {children}
                            </FormContent>
                        </StyledCol>
                    </Row>
                </Content>
            </ThemedComponent>
        </>
    );
}
